import i18next from 'i18next';
import Compressor from 'compressorjs';
import dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import {toast, ExternalToast} from 'sonner';
import {CoreError, DATE_FORMAT, PrivacyPolicySettings} from '@guestapp/sdk';
import {getErrorMessage, LANGUAGES_ISO_3} from '@guestapp/core';

const transformSizePropToSize = (size?: string | number) => {
  if (!size) {
    return;
  } else if (typeof size === 'number') {
    return `${size}px`;
  } else if (size === 'auto') {
    return size;
  } else if (size.slice(-2) === 'px') {
    return size;
  } else {
    return `${size}px`;
  }
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function copyToClipboard(value: string | number) {
  const el = document.createElement('textarea');
  el.value = typeof value === 'number' ? value.toString() : value;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

function dataURIToBlob(dataURI?: string | null) {
  if (!dataURI) {
    return;
  }
  const splitDataURI = dataURI.split(',');
  const byteString =
    splitDataURI[0].indexOf('base64') >= 0
      ? window.atob(splitDataURI[1])
      : decodeURI(splitDataURI[1]);
  const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

  return new Blob([ia], {type: mimeString});
}

function getSignaturePayLoadValue(
  signature: string | null,
): string | Blob | undefined | null {
  if (signature) {
    return dataURIToBlob(signature);
  }
  if (!signature && typeof signature === 'string') {
    return '';
  }

  return;
}

const getPayloadDate = (date: Date | null) => {
  return date ? dayjs(new Date(date)).format(DATE_FORMAT) : undefined;
};

function downloadFromLink(link?: string, blank: '_blank' | '_no-blank' = '_blank') {
  if (!link) return;
  if (blank === '_no-blank') {
    window.location.href = link;
    return;
  }

  const isSwitched = window.open(link, blank);

  if (!isSwitched) {
    const anchorLink = document.createElement('a');
    anchorLink.href = link;
    anchorLink.click();
    anchorLink.remove();
  }
}

function goToExternalLink(url: string) {
  const isSwitched = window.open(url, '_blank');

  if (!isSwitched) {
    const anchorLink = document.createElement('a');
    anchorLink.href = url;
    anchorLink.target = '_blank';
    anchorLink.click();
    anchorLink.remove();
  }
}

function getCurrentLocale() {
  try {
    return i18next.language.slice(0, 2);
  } catch {
    return 'en';
  }
}

function toBase64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Failed to read file as base64'));
  });
}

function getFileSizeMB(file?: File | Blob) {
  if (!file) return;
  return file.size / Math.pow(1024, 2);
}

function getCanvasBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob | null>(function (resolve) {
    canvas.toBlob(function (blob) {
      resolve(blob);
    });
  });
}

type CompressFileOptions = {convertSize?: number};
function compressFile(file: Blob, {convertSize}: CompressFileOptions) {
  return new Promise<File>((resolve, reject) => {
    new Compressor(file, {
      convertSize: convertSize ? convertSize * 1000000 : undefined,
      quality: 1,
      success: (file: File) => resolve(file),
      error: reject,
    });
  });
}

function getStringWithoutNumbers(string = '') {
  return string.replace(/\d/g, '');
}

const getDocumentDownloadByLanguage = (data: PrivacyPolicySettings, language: string) => {
  const defaultLanguage = data.default_language;
  const documents = data.documents;

  const documentInCurrentLanguage = documents.find(document => {
    return document.language === LANGUAGES_ISO_3[language] || false;
  });

  if (documentInCurrentLanguage) {
    return documentInCurrentLanguage.document_download;
  }

  const documentInDefaultLanguage = documents.find(
    document => document.language === defaultLanguage,
  );

  if (documentInDefaultLanguage) {
    return documentInDefaultLanguage.document_download;
  }

  return '';
};

function checkIfCurrentDayBetween(startTime?: string, endTime?: string): boolean {
  dayjs.extend(timezone.default);
  if (!startTime || !endTime) return false;
  const now = dayjs.utc(new Date());
  const localTimezone = dayjs.tz.guess();
  const offset = dayjs().tz(localTimezone).utcOffset();
  const offsetInHours = offset / 60;
  const adjustedDate = now.add(offsetInHours, 'hour');
  return dayjs(adjustedDate).isBetween(dayjs(startTime), dayjs(endTime));
}

function toStripeAmount(value?: string | number | null) {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (numericValue === undefined || numericValue === null || isNaN(numericValue)) {
    return 0;
  }

  return Math.round(numericValue * 100);
}

export function addSupportEmailToMessage(message?: string, prefixText?: string) {
  if (typeof message !== 'string') {
    return message;
  }
  return `${prefixText ? `${prefixText}` : ''}${message}`;
}

function toastResponseError(
  error: unknown,
  options: ExternalToast & {
    prefixText?: string;
  } = {},
) {
  const message = addSupportEmailToMessage(
    getErrorMessage(error as CoreError),
    options?.prefixText,
  );
  const id =
    (error as Error)?.message || (typeof error === 'string' && error) || 'toastId';

  toast.error(message, {
    id,
    ...options,
  });
}

const sanitizeStringFromDashes = (string?: string | null) => {
  return string?.replaceAll('-', '') ?? '';
};

const buildSelectOption = (value?: string) => {
  if (value) {
    return {label: '', value: value};
  }
};

function isAdult(birthDate: string) {
  const birthDateParsed = dayjs(birthDate);
  const today = dayjs();
  const age = today.diff(birthDateParsed, 'year');
  return age >= 18;
}

export {
  transformSizePropToSize,
  isBrowser,
  copyToClipboard,
  dataURIToBlob,
  getSignaturePayLoadValue,
  getPayloadDate,
  downloadFromLink,
  getCurrentLocale,
  getFileSizeMB,
  toBase64,
  getCanvasBlob,
  compressFile,
  getStringWithoutNumbers,
  getDocumentDownloadByLanguage,
  checkIfCurrentDayBetween,
  toStripeAmount,
  goToExternalLink,
  toastResponseError,
  sanitizeStringFromDashes,
  buildSelectOption,
  isAdult,
};
